import { Competitor, CompetitorProductList } from '@gooduncles/gu-app-schema';
import { Select, message } from 'antd';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';

import { errorObjectToString } from 'src/utils/common';
import { createCompetitorProductMapping } from 'src/utils/firebase/firebase-short-cut';

interface CompetitorProductSelectorProps {
  competitor: Competitor;
  competitorProductList: CompetitorProductList | null;
  productId: string;
  selectedProductName: string | null;
  stopEditing: () => void;
}

const CompetitorProductSelector: FC<CompetitorProductSelectorProps> = ({
  competitor,
  competitorProductList,
  productId,
  selectedProductName,
  stopEditing,
}) => {
  const selectRef = useRef<any>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const productList = useMemo(() => competitorProductList?.productList ?? {}, [competitorProductList]);

  const selectedValue = useMemo(() => {
    if (!selectedProductName) return null;
    return productList[selectedProductName] ?? null;
  }, [productList, selectedProductName]);

  const onProductSelect = useCallback(
    async (value: string, option: { label: string; value: string }) => {
      const key = 'createCompetitorProductPriceTrend';
      try {
        if (!option) {
          throw new Error(`${competitor} 상품 정보를 찾지 못했습니다.`);
        }
        await createCompetitorProductMapping({
          competitor,
          guProductId: productId,
          productName: option.label,
          productId: value,
        });
        stopEditing();
      } catch (error) {
        console.error(error);
        const description = errorObjectToString(error);
        messageApi.open({ key, type: 'error', content: description });
      }
    },
    [competitor, messageApi, productId, stopEditing]
  );

  useEffect(() => {
    if (selectRef?.current) {
      selectRef.current.focus();
    }
  }, [selectRef]);

  return (
    <>
      {contextHolder}
      <Select
        showSearch
        ref={selectRef}
        loading={Object.keys(productList).length === 0}
        placeholder={`${competitor} 상품 선택`}
        size={'large'}
        value={selectedValue}
        onSelect={onProductSelect}
        style={{ width: '300px' }}
        options={Object.entries(productList).map(([name, id]) => {
          return { label: name, value: id };
        })}
        filterOption={(input, option) => ((option?.label ?? '') as string).includes(input)}
      />
    </>
  );
};

export default CompetitorProductSelector;
